import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FinancingOptionsSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [selectedOption, setSelectedOption] = useState('quarterly');

  const translations = {
    en: {
      title: "Flexible Financing Options",
      subtitle: "Multiple payment plans designed to fit your cash flow needs",
      options: {
        quarterly: {
          title: "Quarterly Installments",
          subtitle: "Pay every 3 months",
          discount: "5% discount",
          description: "Perfect for businesses with quarterly revenue cycles",
          features: [
            "Pay every 3 months",
            "5% discount on annual billing",
            "Flexible payment dates",
            "No processing fees"
          ]
        },
        lease: {
          title: "Equipment Lease",
          subtitle: "Lease with buyout option",
          discount: "Tax benefits",
          description: "Ideal for preserving working capital",
          features: [
            "Low monthly payments",
            "Tax deductible expenses",
            "Buyout option available",
            "Preserve credit line"
          ]
        },
        subsidy: {
          title: "Government Subsidy",
          subtitle: "MSME benefits available",
          discount: "Up to 50% subsidy",
          description: "Leverage government schemes for MSMEs",
          features: [
            "Technology upgrade subsidy",
            "MSME development schemes",
            "State government incentives",
            "Consultation included"
          ]
        }
      },
      msmeSection: {
        title: "Special MSME Benefits",
        subtitle: "Exclusive offers for Micro, Small & Medium Enterprises",
        benefits: [
          {
            icon: "Percent",
            title: "25% Discount",
            description: "Special pricing for MSME registered businesses"
          },
          {
            icon: "Clock",
            title: "Extended Payment Terms",
            description: "Up to 90 days payment terms for qualified MSMEs"
          },
          {
            icon: "Award",
            title: "Priority Support",
            description: "Dedicated MSME support team and faster response times"
          },
          {
            icon: "BookOpen",
            title: "Free Training",
            description: "Extended training programs and skill development"
          }
        ]
      },
      subsidyPrograms: {
        title: "Government Subsidy Programs",
        programs: [
          {
            name: "Technology Upgradation Fund Scheme (TUFS)",
            subsidy: "Up to 15%",
            description: "For technology upgrade and modernization"
          },
          {
            name: "Credit Linked Capital Subsidy (CLCS)",
            subsidy: "Up to 25%",
            description: "For MSMEs adopting new technology"
          },
          {
            name: "State Government Incentives",
            subsidy: "Varies by state",
            description: "Additional state-specific benefits available"
          }
        ]
      },
      cta: {
        title: "Ready to Get Started?",
        subtitle: "Talk to our financing experts to find the best option for your business",
        button: "Schedule Consultation"
      }
    },
    hi: {
      title: "लचीले वित्तपोषण विकल्प",
      subtitle: "आपकी नकदी प्रवाह आवश्यकताओं के अनुकूल कई भुगतान योजनाएं",
      options: {
        quarterly: {
          title: "त्रैमासिक किस्तें",
          subtitle: "हर 3 महीने में भुगतान",
          discount: "5% छूट",
          description: "त्रैमासिक राजस्व चक्र वाले व्यवसायों के लिए सही",
          features: [
            "हर 3 महीने में भुगतान",
            "वार्षिक बिलिंग पर 5% छूट",
            "लचीली भुगतान तिथियां",
            "कोई प्रोसेसिंग फीस नहीं"
          ]
        },
        lease: {
          title: "उपकरण लीज़",
          subtitle: "खरीदारी विकल्प के साथ लीज़",
          discount: "कर लाभ",
          description: "कार्यशील पूंजी संरक्षण के लिए आदर्श",
          features: [
            "कम मासिक भुगतान",
            "कर कटौती योग्य खर्च",
            "खरीदारी विकल्प उपलब्ध",
            "क्रेडिट लाइन संरक्षण"
          ]
        },
        subsidy: {
          title: "सरकारी सब्सिडी",
          subtitle: "MSME लाभ उपलब्ध",
          discount: "50% तक सब्सिडी",
          description: "MSMEs के लिए सरकारी योजनाओं का लाभ उठाएं",
          features: [
            "प्रौद्योगिकी उन्नयन सब्सिडी",
            "MSME विकास योजनाएं",
            "राज्य सरकार प्रोत्साहन",
            "परामर्श शामिल"
          ]
        }
      },
      msmeSection: {
        title: "विशेष MSME लाभ",
        subtitle: "सूक्ष्म, लघु और मध्यम उद्यमों के लिए विशेष ऑफर",
        benefits: [
          {
            icon: "Percent",
            title: "25% छूट",
            description: "MSME पंजीकृत व्यवसायों के लिए विशेष मूल्य निर्धारण"
          },
          {
            icon: "Clock",
            title: "विस्तारित भुगतान शर्तें",
            description: "योग्य MSMEs के लिए 90 दिन तक की भुगतान शर्तें"
          },
          {
            icon: "Award",
            title: "प्राथमिकता सहायता",
            description: "समर्पित MSME सहायता टीम और तेज़ प्रतिक्रिया समय"
          },
          {
            icon: "BookOpen",
            title: "मुफ्त प्रशिक्षण",
            description: "विस्तारित प्रशिक्षण कार्यक्रम और कौशल विकास"
          }
        ]
      },
      subsidyPrograms: {
        title: "सरकारी सब्सिडी कार्यक्रम",
        programs: [
          {
            name: "टेक्नोलॉजी अपग्रेडेशन फंड स्कीम (TUFS)",
            subsidy: "15% तक",
            description: "प्रौद्योगिकी उन्नयन और आधुनिकीकरण के लिए"
          },
          {
            name: "क्रेडिट लिंक्ड कैपिटल सब्सिडी (CLCS)",
            subsidy: "25% तक",
            description: "नई प्रौद्योगिकी अपनाने वाले MSMEs के लिए"
          },
          {
            name: "राज्य सरकार प्रोत्साहन",
            subsidy: "राज्य के अनुसार",
            description: "अतिरिक्त राज्य-विशिष्ट लाभ उपलब्ध"
          }
        ]
      },
      cta: {
        title: "शुरू करने के लिए तैयार?",
        subtitle: "अपने व्यवसाय के लिए सबसे अच्छा विकल्प खोजने के लिए हमारे वित्तपोषण विशेषज्ञों से बात करें",
        button: "परामर्श शेड्यूल करें"
      }
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  return (
    <section id="financing-options" className="py-20 bg-surface-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            {t.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Financing Options */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {Object.entries(t.options).map(([key, option]) => (
            <div 
              key={key}
              className={`bg-white rounded-2xl p-8 shadow-lg border cursor-pointer transition-all duration-300 ${
                selectedOption === key ? 'border-primary shadow-xl' : 'border-border hover:shadow-xl'
              }`}
              onClick={() => setSelectedOption(key)}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon 
                    name={key === 'quarterly' ? 'Calendar' : key === 'lease' ? 'CreditCard' : 'Award'} 
                    size={24} 
                    color="var(--color-primary)" 
                  />
                </div>
                <h3 className="text-xl font-bold text-primary mb-2">
                  {option.title}
                </h3>
                <p className="text-text-secondary mb-2">
                  {option.subtitle}
                </p>
                <div className="inline-flex items-center bg-accent-100 text-accent px-3 py-1 rounded-full text-sm font-medium">
                  {option.discount}
                </div>
              </div>

              <p className="text-text-secondary text-center mb-6">
                {option.description}
              </p>

              <div className="space-y-3">
                {option.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-primary-100 rounded-full flex items-center justify-center">
                      <Icon name="Check" size={12} color="var(--color-primary)" />
                    </div>
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* MSME Benefits */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-border mb-16">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-primary mb-4">
              {t.msmeSection.title}
            </h3>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t.msmeSection.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.msmeSection.benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={benefit.icon} size={24} color="var(--color-success)" />
                </div>
                <h4 className="text-lg font-bold text-success mb-2">
                  {benefit.title}
                </h4>
                <p className="text-sm text-text-secondary">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Government Subsidy Programs */}
        <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-2xl p-8 mb-16">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-primary mb-4">
              {t.subsidyPrograms.title}
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {t.subsidyPrograms.programs.map((program, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-bold text-primary">
                    {program.name}
                  </h4>
                  <div className="bg-accent-100 text-accent px-3 py-1 rounded-full text-sm font-medium">
                    {program.subsidy}
                  </div>
                </div>
                <p className="text-text-secondary text-sm">
                  {program.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-border text-center">
          <div className="max-w-2xl mx-auto">
            <Icon name="MessageCircle" size={48} color="var(--color-primary)" className="mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-primary mb-4">
              {t.cta.title}
            </h3>
            <p className="text-text-secondary mb-8">
              {t.cta.subtitle}
            </p>
            <Button
              variant="primary"
              size="lg"
              className="bg-gradient-accent hover:bg-accent-600 shadow-cta"
              iconName="Calendar"
              iconPosition="left"
            >
              {t.cta.button}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinancingOptionsSection;